import { useMemo, useState } from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { BadgeAlert, CheckCircle2, Clock3, History, MessageSquareMore, Undo2 } from 'lucide-react';
import { OperationalSummaryCards } from './OperationalSummaryCards';

const statusClass = {
  pending: 'bg-amber-50 text-amber-700 border-amber-200',
  in_progress: 'bg-sky-50 text-sky-700 border-sky-200',
  completed: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  justified: 'bg-violet-50 text-violet-700 border-violet-200',
  correction_requested: 'bg-orange-50 text-orange-700 border-orange-200',
  corrected: 'bg-green-50 text-green-700 border-green-200',
};

const OPEN_STATUSES = new Set(['pending', 'in_progress', 'correction_requested']);
const innerTabsListClass = 'flex h-auto w-full flex-wrap justify-start gap-4 rounded-none border-b border-slate-200 bg-transparent px-3.5 py-0';
const innerTabsTriggerClass = 'h-9 rounded-none border-b-2 border-transparent px-0 text-xs font-medium uppercase tracking-[0.12em] text-slate-500 shadow-none data-[state=active]:border-slate-900 data-[state=active]:bg-transparent data-[state=active]:text-slate-900';

const formatOperationalDateTime = (value) => {
  if (!value) return '—';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleString('pt-PT', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const initialActionState = { open: false, mode: '', activity: null, comment: '', executed_at: '', reason_id: '', request_note: '', requested_status: '' };

export const OperationalWorkerTab = ({ workspace, reasons, onComplete, onJustify, onComment, onRequestCorrection }) => {
  const [actionState, setActionState] = useState(initialActionState);
  const quickReasons = useMemo(() => reasons || [], [reasons]);
  const allActivityRows = useMemo(() => ([...(workspace?.today || []), ...(workspace?.backlog || [])]), [workspace?.backlog, workspace?.today]);
  const pendingRows = useMemo(() => allActivityRows.filter((item) => OPEN_STATUSES.has(item.status)), [allActivityRows]);
  const completedRows = useMemo(() => allActivityRows.filter((item) => ['completed', 'corrected'].includes(item.status)), [allActivityRows]);
  const justifiedRows = useMemo(() => allActivityRows.filter((item) => item.status === 'justified'), [allActivityRows]);
  const historyRows = workspace?.timeline || [];
  const historyHighlights = useMemo(() => {
    const counts = historyRows.reduce((accumulator, event) => {
      const key = String(event.action || 'evento');
      accumulator[key] = (accumulator[key] || 0) + 1;
      return accumulator;
    }, {});
    return Object.entries(counts).slice(0, 5);
  }, [historyRows]);

  const openAction = (mode, activity) => setActionState({
    open: true,
    mode,
    activity,
    comment: '',
    executed_at: '',
    reason_id: quickReasons[0]?.id || '',
    request_note: '',
    requested_status: activity?.status || 'pending',
  });

  const closeAction = () => setActionState(initialActionState);

  const submitAction = async () => {
    if (!actionState.activity) return;
    if (actionState.mode === 'complete') {
      await onComplete(actionState.activity.id, { executed_at: actionState.executed_at || null, comment: actionState.comment || null });
    }
    if (actionState.mode === 'justify') {
      await onJustify(actionState.activity.id, { reason_id: actionState.reason_id, comment: actionState.comment, executed_at: actionState.executed_at || null });
    }
    if (actionState.mode === 'comment') {
      await onComment(actionState.activity.id, { comment: actionState.comment });
    }
    if (actionState.mode === 'correction') {
      await onRequestCorrection(actionState.activity.id, {
        requested_status: actionState.requested_status || null,
        requested_executed_at: actionState.executed_at || null,
        requested_comment: actionState.comment || null,
        requested_reason_id: actionState.reason_id || null,
        request_note: actionState.request_note,
      });
    }
    closeAction();
  };

  const renderActivityRow = (activity, scope) => (
    <div key={activity.id} className="rounded-lg border border-slate-200 bg-white px-3 py-2 transition-colors hover:bg-slate-50/80" data-testid={`operational-worker-${scope}-activity-${activity.id}`}>
      <div className="flex flex-wrap items-start justify-between gap-2.5">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-1.5">
            <p className="truncate text-[13px] font-semibold text-slate-900">{activity.title}</p>
            <Badge className={statusClass[activity.status] || statusClass.pending} variant="outline" data-testid={`operational-worker-${scope}-activity-status-${activity.id}`}>{activity.status_label || activity.status}</Badge>
            <Badge variant="outline" className="border-slate-200 bg-slate-50 text-[11px] text-slate-700" data-testid={`operational-worker-${scope}-activity-priority-${activity.id}`}>{activity.priority}</Badge>
            {(activity.context_signals?.sales?.sales_count || 0) > 0 ? <Badge variant="outline" className="border-sky-200 bg-sky-50 text-sky-700">POS {activity.context_signals?.sales?.sales_count}</Badge> : null}
          </div>
          <p className="mt-1 max-w-4xl text-[11px] leading-5 text-slate-500" data-testid={`operational-worker-${scope}-activity-description-${activity.id}`}>{activity.description || 'Sem descrição adicional.'}</p>
          <div className="mt-2 flex flex-wrap gap-x-3 gap-y-1 text-[10px] text-slate-600">
            <span data-testid={`operational-worker-${scope}-activity-schedule-${activity.id}`}><strong>Data:</strong> {activity.scheduled_date}</span>
            <span data-testid={`operational-worker-${scope}-activity-area-${activity.id}`}><strong>Área:</strong> {activity.area || '—'}</span>
            <span data-testid={`operational-worker-${scope}-activity-role-${activity.id}`}><strong>Função:</strong> {activity.function_label || '—'}</span>
            <span data-testid={`operational-worker-${scope}-activity-store-${activity.id}`}><strong>Loja:</strong> {activity.store_location || 'Empresa geral'}</span>
            {activity.context_signals?.sales?.latest_sale_time ? <span data-testid={`operational-worker-${scope}-activity-sales-context-${activity.id}`}><strong>Última venda:</strong> {activity.context_signals?.sales?.latest_sale_time}</span> : null}
          </div>
          {Array.isArray(activity.comment_suggestions) && activity.comment_suggestions.length > 0 ? (
            <p className="mt-2 truncate rounded-md bg-slate-50 px-2.5 py-1.5 text-[11px] text-slate-500" data-testid={`operational-worker-${scope}-activity-comment-suggestions-${activity.id}`}>
              Sugestão: {activity.comment_suggestions[0]}
            </p>
          ) : null}
        </div>

        <div className="flex flex-wrap items-center gap-1.5 xl:max-w-[316px] xl:justify-end">
          <Button size="sm" className="h-7 rounded-md bg-slate-900 px-2.5 text-xs text-white hover:bg-slate-800" onClick={() => openAction('complete', activity)} data-testid={`operational-complete-activity-${activity.id}`}>
            <CheckCircle2 className="mr-1.5 h-4 w-4" /> Concluir
          </Button>
          <Button size="sm" variant="outline" className="h-7 rounded-md border-slate-200 px-2.5 text-xs text-slate-900 hover:bg-slate-50" onClick={() => openAction('justify', activity)} data-testid={`operational-justify-activity-${activity.id}`}>
            <BadgeAlert className="mr-1.5 h-4 w-4" /> Justificar
          </Button>
          <Button size="sm" variant="outline" className="h-7 rounded-md border-slate-200 px-2.5 text-xs text-slate-900 hover:bg-slate-50" onClick={() => openAction('comment', activity)} data-testid={`operational-comment-activity-${activity.id}`}>
            <MessageSquareMore className="mr-1.5 h-4 w-4" /> Comentário
          </Button>
          <Button size="sm" variant="outline" className="h-7 rounded-md border-slate-200 px-2.5 text-xs text-slate-900 hover:bg-slate-50" onClick={() => openAction('correction', activity)} data-testid={`operational-correction-activity-${activity.id}`}>
            <Undo2 className="mr-1.5 h-4 w-4" /> Correcção
          </Button>
        </div>
      </div>
    </div>
  );

  const renderSection = (title, rows, scope, testId) => (
    <Card className="rounded-lg border border-slate-200 bg-white shadow-sm" data-testid={testId}>
      <CardHeader className="flex flex-row items-center justify-between border-b border-slate-200 px-3 py-2">
        <div>
          <CardTitle className="text-base font-semibold text-slate-900">{title}</CardTitle>
          <p className="mt-1 text-xs text-slate-500">Lista operacional compacta com leitura rápida e acções directas.</p>
        </div>
        <Badge variant="outline" className="border-slate-200 bg-slate-50 text-slate-700">{rows.length}</Badge>
      </CardHeader>
      <CardContent className="space-y-2 px-3 py-3">
        {rows.length === 0 ? <p className="text-sm text-slate-500" data-testid={`${testId}-empty`}>Sem itens nesta secção.</p> : rows.map((activity) => renderActivityRow(activity, scope))}
      </CardContent>
    </Card>
  );

  return (
    <div className="space-y-2" data-testid="operational-worker-tab">
      <OperationalSummaryCards summary={workspace?.summary} testIdPrefix="operational-worker-summary" />

      <Tabs defaultValue="today" className="space-y-2" data-testid="operational-worker-subtabs">
        <div className="rounded-lg border border-slate-200 bg-white shadow-sm" data-testid="operational-worker-subtabs-shell">
          <TabsList className={innerTabsListClass} data-testid="operational-worker-subtabs-list">
            <TabsTrigger value="today" className={innerTabsTriggerClass} data-testid="operational-worker-subtab-today-trigger">Hoje</TabsTrigger>
            <TabsTrigger value="pending" className={innerTabsTriggerClass} data-testid="operational-worker-subtab-pending-trigger">Pendentes</TabsTrigger>
            <TabsTrigger value="completed" className={innerTabsTriggerClass} data-testid="operational-worker-subtab-completed-trigger">Concluídas</TabsTrigger>
            <TabsTrigger value="justifications" className={innerTabsTriggerClass} data-testid="operational-worker-subtab-justifications-trigger">Justificações</TabsTrigger>
            <TabsTrigger value="history" className={innerTabsTriggerClass} data-testid="operational-worker-subtab-history-trigger">Histórico</TabsTrigger>
          </TabsList>
        </div>

        <TabsContent value="today" className="mt-0" data-testid="operational-worker-subtab-today-content">
          <div className="grid gap-2 xl:grid-cols-[minmax(0,1.6fr)_320px]">
            <div className="space-y-2">
              <Card className="rounded-lg border border-slate-200 bg-white shadow-sm" data-testid="operational-worker-suggestions-card">
                <CardHeader className="border-b border-slate-200 px-3 py-2">
                  <CardTitle className="text-base font-semibold text-slate-900">Sugestões automáticas do dia</CardTitle>
                </CardHeader>
                <CardContent className="space-y-1.5 px-3 py-3 text-sm text-slate-700">
                  {(workspace?.priority_suggestions || []).length === 0 ? (
                    <p data-testid="operational-worker-suggestions-empty">Sem prioridades críticas abertas neste momento.</p>
                  ) : workspace.priority_suggestions.map((item) => (
                    <div key={item.activity_instance_id} className="rounded-md border border-slate-200 bg-slate-50 px-2.5 py-2" data-testid={`operational-priority-suggestion-${item.activity_instance_id}`}>
                      <p className="text-[13px] font-medium text-slate-900">{item.title}</p>
                      <p className="mt-0.5 text-[11px] text-slate-600">{item.reason}</p>
                    </div>
                  ))}
                </CardContent>
              </Card>

              {renderSection('Actividades de hoje', workspace?.today || [], 'today', 'operational-worker-today-card')}
            </div>

            <Card className="sticky top-3 h-fit rounded-lg border border-slate-200 bg-white shadow-sm" data-testid="operational-worker-timeline-card">
              <CardHeader className="border-b border-slate-200 px-3 py-2">
                <CardTitle className="flex items-center gap-2 text-base font-semibold text-slate-900"><History className="h-4 w-4" /> Timeline auditável</CardTitle>
              </CardHeader>
              <CardContent className="space-y-1.5 px-3 py-3">
                {historyRows.length === 0 ? <p data-testid="operational-worker-timeline-empty">Sem eventos auditáveis ainda.</p> : historyRows.map((event, index) => (
                  <div key={event.id || `${event.entity_id}-${index}`} className="rounded-md border border-slate-200 bg-slate-50 px-2.5 py-1.5 text-sm" data-testid={`operational-worker-timeline-event-${index}`}>
                    <div className="flex items-center gap-2">
                      <Clock3 className="h-3.5 w-3.5 text-slate-400" />
                      <p className="font-medium text-slate-900">{event.action}</p>
                    </div>
                    <p className="mt-1 text-[11px] text-slate-500">{formatOperationalDateTime(event.created_at)}</p>
                    <p className="mt-1 text-[11px] text-slate-700">{event.reason || 'Sem motivo adicional'}</p>
                  </div>
                ))}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="pending" className="mt-0" data-testid="operational-worker-subtab-pending-content">
          <div className="grid gap-2 xl:grid-cols-[minmax(0,1.55fr)_300px]">
            <div className="space-y-2">
              {renderSection('Pendências activas', pendingRows, 'pending', 'operational-worker-pending-card')}
              {renderSection('Backlog acumulado', workspace?.backlog || [], 'backlog', 'operational-worker-backlog-card')}
            </div>

            <Card className="rounded-lg border border-slate-200 bg-white shadow-sm" data-testid="operational-worker-pending-focus-card">
              <CardHeader className="border-b border-slate-200 px-3 py-2">
                <CardTitle className="text-base font-semibold text-slate-900">Leitura rápida</CardTitle>
              </CardHeader>
              <CardContent className="space-y-1.5 px-3 py-3">
                <div className="rounded-md border border-slate-200 bg-slate-50 px-2.5 py-2" data-testid="operational-worker-pending-focus-open">
                  <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-500">Abertas</p>
                  <p className="mt-1 text-2xl font-semibold text-slate-950">{pendingRows.length}</p>
                </div>
                <div className="rounded-md border border-slate-200 bg-slate-50 px-2.5 py-2" data-testid="operational-worker-pending-focus-backlog">
                  <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-500">Backlog</p>
                  <p className="mt-1 text-2xl font-semibold text-slate-950">{(workspace?.backlog || []).length}</p>
                </div>
                <p className="text-xs leading-5 text-slate-500" data-testid="operational-worker-pending-focus-note">Esta subAba junta o que ainda exige seguimento directo, reduzindo a dispersão entre hoje e histórico.</p>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="completed" className="mt-0" data-testid="operational-worker-subtab-completed-content">
          {renderSection('Concluídas e regularizadas', completedRows, 'completed', 'operational-worker-completed-card')}
        </TabsContent>

        <TabsContent value="justifications" className="mt-0" data-testid="operational-worker-subtab-justifications-content">
          <div className="grid gap-2 xl:grid-cols-[minmax(0,1.55fr)_300px]">
            {renderSection('Actividades justificadas', justifiedRows, 'justified', 'operational-worker-justified-card')}

            <Card className="rounded-lg border border-slate-200 bg-white shadow-sm" data-testid="operational-worker-reasons-card">
              <CardHeader className="border-b border-slate-200 px-3 py-2">
                <CardTitle className="text-base font-semibold text-slate-900">Motivos rápidos</CardTitle>
              </CardHeader>
              <CardContent className="space-y-1.5 px-3 py-3">
                {quickReasons.length === 0 ? <p className="text-sm text-slate-500" data-testid="operational-worker-reasons-empty">Sem motivos rápidos disponíveis.</p> : quickReasons.slice(0, 8).map((reason) => (
                  <div key={reason.id} className="rounded-md border border-slate-200 bg-slate-50 px-2.5 py-1.5" data-testid={`operational-worker-reason-${reason.id}`}>
                    <p className="text-sm font-medium text-slate-900">{reason.label}</p>
                    <p className="mt-1 text-[11px] text-slate-500">{reason.description || 'Sem descrição adicional.'}</p>
                  </div>
                ))}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="history" className="mt-0" data-testid="operational-worker-subtab-history-content">
          <div className="grid gap-2 xl:grid-cols-[minmax(0,1.5fr)_300px]">
            <Card className="rounded-lg border border-slate-200 bg-white shadow-sm" data-testid="operational-worker-history-timeline-card">
              <CardHeader className="border-b border-slate-200 px-3 py-2">
                <CardTitle className="text-base font-semibold text-slate-900">Histórico auditável</CardTitle>
              </CardHeader>
              <CardContent className="space-y-1.5 px-3 py-3">
                {historyRows.length === 0 ? <p className="text-sm text-slate-500" data-testid="operational-worker-history-empty">Sem histórico disponível.</p> : historyRows.map((event, index) => (
                  <div key={event.id || `${event.entity_id}-${index}`} className="rounded-md border border-slate-200 bg-slate-50 px-2.5 py-1.5" data-testid={`operational-worker-history-row-${index}`}>
                    <div className="flex items-center gap-2">
                      <Clock3 className="h-3.5 w-3.5 text-slate-400" />
                      <p className="text-sm font-medium text-slate-900">{event.action}</p>
                    </div>
                    <p className="mt-1 text-[11px] text-slate-500">{formatOperationalDateTime(event.created_at)}</p>
                    <p className="mt-1 text-[11px] text-slate-700">{event.reason || 'Sem motivo adicional'}</p>
                  </div>
                ))}
              </CardContent>
            </Card>

            <Card className="rounded-lg border border-slate-200 bg-white shadow-sm" data-testid="operational-worker-history-summary-card">
              <CardHeader className="border-b border-slate-200 px-3 py-2">
                <CardTitle className="text-base font-semibold text-slate-900">Síntese do histórico</CardTitle>
              </CardHeader>
              <CardContent className="space-y-1.5 px-3 py-3">
                {historyHighlights.length === 0 ? <p className="text-sm text-slate-500" data-testid="operational-worker-history-summary-empty">Sem eventos agregados.</p> : historyHighlights.map(([action, count]) => (
                  <div key={action} className="flex items-center justify-between rounded-md border border-slate-200 bg-slate-50 px-3 py-2" data-testid={`operational-worker-history-summary-${action}`}>
                    <p className="text-sm font-medium text-slate-900">{action}</p>
                    <Badge variant="outline" className="border-slate-200 bg-white text-slate-700">{count}</Badge>
                  </div>
                ))}
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>

      <Dialog open={actionState.open} onOpenChange={(open) => (!open ? closeAction() : null)}>
        <DialogContent className="max-w-xl border-slate-200 bg-white shadow-2xl" data-testid="operational-worker-action-dialog">
          <DialogHeader>
            <DialogTitle className="text-lg font-semibold text-slate-900" data-testid="operational-worker-action-dialog-title">
              {actionState.mode === 'complete' ? 'Concluir actividade' : actionState.mode === 'justify' ? 'Justificar não cumprimento' : actionState.mode === 'comment' ? 'Adicionar comentário' : 'Pedir correcção'}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-3">
            {(actionState.mode === 'complete' || actionState.mode === 'justify' || actionState.mode === 'correction') ? (
              <div className="space-y-2">
                <Label htmlFor="operational-action-executed-at">Data / hora real</Label>
                <Input id="operational-action-executed-at" type="datetime-local" value={actionState.executed_at} onChange={(event) => setActionState((prev) => ({ ...prev, executed_at: event.target.value }))} data-testid="operational-action-executed-at-input" />
              </div>
            ) : null}

            {(actionState.mode === 'justify' || actionState.mode === 'correction') ? (
              <div className="space-y-2">
                <Label>Motivo rápido</Label>
                <Select value={actionState.reason_id} onValueChange={(value) => setActionState((prev) => ({ ...prev, reason_id: value }))}>
                  <SelectTrigger data-testid="operational-action-reason-select"><SelectValue placeholder="Seleccionar motivo" /></SelectTrigger>
                  <SelectContent>
                    {quickReasons.map((reason) => <SelectItem key={reason.id} value={reason.id}>{reason.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            ) : null}

            {actionState.mode === 'correction' ? (
              <div className="space-y-2">
                <Label>Novo estado pretendido</Label>
                <Select value={actionState.requested_status} onValueChange={(value) => setActionState((prev) => ({ ...prev, requested_status: value }))}>
                  <SelectTrigger data-testid="operational-action-requested-status-select"><SelectValue placeholder="Estado pretendido" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pending">Pendente</SelectItem>
                    <SelectItem value="completed">Concluída</SelectItem>
                    <SelectItem value="justified">Justificada</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            ) : null}

            <div className="space-y-2">
              <Label htmlFor="operational-action-comment">Comentário</Label>
              <Textarea id="operational-action-comment" rows={4} value={actionState.comment} onChange={(event) => setActionState((prev) => ({ ...prev, comment: event.target.value }))} data-testid="operational-action-comment-textarea" />
            </div>

            {actionState.mode === 'correction' ? (
              <div className="space-y-2">
                <Label htmlFor="operational-action-request-note">Motivo do pedido</Label>
                <Textarea id="operational-action-request-note" rows={3} value={actionState.request_note} onChange={(event) => setActionState((prev) => ({ ...prev, request_note: event.target.value }))} data-testid="operational-action-request-note-textarea" />
              </div>
            ) : null}
          </div>

          <DialogFooter className="border-t border-slate-200 pt-4">
            <Button className="rounded-md border-slate-200" variant="outline" onClick={closeAction} data-testid="operational-action-cancel-button">Cancelar</Button>
            <Button className="rounded-md bg-slate-900 text-white hover:bg-slate-800" onClick={submitAction} data-testid="operational-action-submit-button">Guardar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};